import { AfterContentInit, Component, HostBinding, HostListener, Inject, Injector, Input, OnInit, PLATFORM_ID, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { Currency, LandingModel } from 'projects/common/model/landingModel';
import { Module } from 'projects/common/model/ModuleModel';
import { ChatAIService } from 'projects/common/services/chat-AI.service';
import { AbstractComponent } from '../abstract.component';
import { OfferCardData, OfferCardType, OfferViewType } from '../offers/offer-card-data.model';
import { isPlatformBrowser } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { UpaTarget } from 'projects/common/services/upa.service';
import { HttpErrorResponse } from '@angular/common/http';
import * as uuid from 'uuid';
import { CustomTimmingsService } from 'projects/common/services/custom-timming-service.service';

declare var upaData: any;
declare var window: any;
declare var document: any;

export interface ChatAIModuleConfiguration {
    messages: ChatAIServiceMessage[],
    meta_data: ChatAIMetaData,
    context: {[key: string]: string},
    ai_type: string,
    destination_description?: string;
    offers?: OfferCardData[];
    search_link?: string;
    recommendations?: string;
    feedback: ChatAIFeedback;
    version: string;
    sandbox: boolean;
    assistant_id?: string;
    google_map_api_key?: string;
}

export interface ChatAIFeedback {
    message: string;
    link: string;
}

export interface ChatAIMessage extends ChatAIServiceMessage {
    offers?: OfferCardData[];
    search_link?: string;
    recommendations?: string;
    showRecommendationButton?: boolean;
    showRestartButton?: boolean;
    showVideoCallButton?: boolean;
}

export interface ChatAIServiceMessage {
    sender: string;
    message?: string;
    timestamp?: number;
    message_type?: string;
}

export interface ChatAIMetaData {
    root_domain: string;
    site: string;
    product: string;
    conversation_type: string;
    total_messages: number;
    quota_left: number;
    session_id: string;
    conversation_id: string;
}

@Component({
    selector: 'chat-AI',
    template: `
        <div id="chat-assistant" *ngIf="module.data.version === 'V2'">
            <div  class="loader-wrapper">
                <div class="eva-3-loader -lg">
                    <div class="loader-container">
                        <div class="loader-spinner">
                            <div class="spinner-container spinner-left">
                                <div class="spinner-circle"></div>
                            </div>
                            <div class="spinner-container spinner-right">
                                <div class="spinner-circle"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div *ngIf="module.data.version !== 'V2'" class="chat-AI">
            <div class="chat-container">
                <div class="rows-container" #rowsContainer [ngClass]="{'-input-fixed': isFixedInput}">
                    <chat-AI-message *ngFor="let message of messagesForUser; let last = last" class="row {{message.sender}}"
                        [message]="message" [userAvatar]="userAvatar">
                        <ng-container>
                            <div *ngIf="message.offers || message.search_link" class="offers-suggestions">
                                <eva-button *ngIf="message.search_link && onlySearchLink" class="search-button -eva-3-mt-md"
                                    [options]="{type: '-secondary', link: message.search_link, link_text: ('chatAI.viewOffers' | translate), target: '_blank'}"
                                    [moduleId]="getModuleId()" [moduleIndex]="module.row_number" [uniqueId]="module.unique_id" [upaData]="{label: 'VIEW_MORE|' + messagesForService.length}"></eva-button>

                                <div *ngIf="message.offers && message.offers.length > 0" class="offers-recommendations -eva-3-mt-md">
                                    <slider product="nymeria-gui" [flow]="flow"
                                            [ngStyle]="{flexBasis: this.sliderWidth + 'px'}"
                                            [containerWidth]="this.sliderWidth"
                                            [showNavigationOnHover]="false"
                                            [slidesPerView]="'auto'"
                                            [centeredSlides]="screenType !== 'DESKTOP'"
                                            [spaceBetween]="8"
                                            [navigation]="screenType === 'DESKTOP'"
                                            [hideDisabledNav]="true"
                                            [arrowsInsideSwiper]="true"
                                    >
                                        <slider-item *ngFor="let offerData of message.offers; let i = index">
                                            <offer-card-selector style="height: 100%" [data]="offerData" [currency]="currency" [moduleId]="module.id" [moduleIndex]="module.row_number"
                                                        [uniqueId]="module.unique_id || 'empty'" [positionIndex]="i" [offerViewType]="offerViewType" [detailOffer]="true"
                                                        [locale]="locale" [loyaltyMode]="null" [flow]="flow"  [offerCardType]="offerCardType"></offer-card-selector>
                                        </slider-item>

                                        <slider-item class="slider-card" *ngIf="message.search_link">
                                            <a class="eva-3-link eva-3-card -eva-3-center -eva-3-shadow-line see-more-card" [href]="message.search_link">
                                                <eva-button class="-eva-3-mb-sm"
                                                            [newTab]="true"
                                                            [options]="{circle: true, type: '-primary', ghost_button: true, size: '-lg', link: message.search_link, next: true}"
                                                            [moduleId]="module.id" [uniqueId]="module.unique_id"
                                                            [moduleIndex]="module.row_number"
                                                            [upaData]="{label: 'VIEW_MORE|' + messagesForService.length}">
                                                </eva-button>
                                                <span class="-eva-3-mt-sm see-more-text">{{'chatAI.viewMorePacks' | translate}}</span>
                                            </a>
                                        </slider-item>
                                    </slider>
                                </div>
                            </div>

                            <div *ngIf="message.showRecommendationButton || message.showRestartButton || message.showVideoCallButton" class="user-action-buttons -eva-3-mt-md -eva-3-mr-md">
                                <eva-button *ngIf="message.showRecommendationButton" class="recommendation-button" [options]="{type: last ? '-primary' : '-disable', link_text: ('chatAI.viewRecommendations' | translate)}"
                                    [moduleId]="getModuleId()" [moduleIndex]="module.row_number" [uniqueId]="module.unique_id" (clickEvent)="last && showRecommendations()"
                                    [upaData]="{label: 'SHOW_RECOMMENDATIONS|' + messagesForService.length}"></eva-button>

                              <eva-button *ngIf="message.showVideoCallButton" class="recommendation-button" [options]="{type: last ? '-primary' : '-disable', link_text: ('chatAI.videoCall.text' | translate), link: ('chatAI.videoCall.link' | translate)}"
                                          [moduleId]="getModuleId()" [moduleIndex]="module.row_number" [uniqueId]="module.unique_id"
                                          [upaData]="{label: 'ASK_FOR_VIDEO_CALL|' + messagesForService.length}"></eva-button>

                                <eva-button *ngIf="message.showRestartButton" class="recommendation-button" [options]="{type: last ? '-primary' : '-disable', link_text: ('chatAI.restartConversation' | translate), icon: 'refresh'}"
                                    [moduleId]="getModuleId()" [moduleIndex]="module.row_number" [uniqueId]="module.unique_id" (clickEvent)="last && restart()"
                                    [upaData]="{label: 'RESTART|' + messagesForService.length}"></eva-button>
                            </div>

                            <div *ngIf="message.recommendations" class="message" [innerHTML]="message.recommendations"></div>
                        </ng-container>
                    </chat-AI-message>

                    <chat-AI-message *ngIf="showSystemLoader" class="row system" [message]="{sender: 'system'}">
                        <div class="message">
                            <div class="loader"><em *ngIf="slowResponseMessage">{{'chatAI.slowResponse' | translate}}</em>.</div>
                        </div>
                    </chat-AI-message>

                    <chat-AI-message *ngIf="showSystemError" class="row system"
                        [message]="{sender: 'system', message: ('chatAI.error' | translate)}">

                        <eva-button class="recommendation-button -eva-3-mt-md" [options]="{type: '-primary', link_text: ('chatAI.restartConversation' | translate), icon: 'refresh'}"
                            [moduleId]="getModuleId()" [moduleIndex]="module.row_number" [uniqueId]="module.unique_id" (clickEvent)="restart()"
                            [upaData]="{label: 'RESTART_ERROR|' + messagesForService.length}"></eva-button>
                    </chat-AI-message>
                </div>

                <div class="input-box-wrapper"
                    [ngClass]="{'-fixed': isFixedInput, '-finished': finishedConversation}"
                    (touchstart)="onTouchStart()">

                    <div class="input-row">
                        <div class="eva-3-textarea -lg">
                            <div class="textarea-container">
                                <textarea class="textarea-user-input textarea-tag" [maxLength]="maxCharactersToInput" [(ngModel)]="inputForUser" [disabled]="disableButton || finishedConversation"
                                    (keyup)="onKeyUp($event)" (keypress)="$event.which !== 13" [placeholder]="'chatAI.placeholder' | translate"></textarea>
                            </div>
                        </div>

                        <eva-button class="button-send"
                            [options]="{type: disableButton ? '-disable' : '-primary', size: screenType === 'MOBILE' ? '-md' : '-lg', circle: true, icon: 'send'}"
                            (clickEvent)="send()" [moduleId]="getModuleId()" [moduleIndex]="module.row_number" [uniqueId]="module.unique_id" ></eva-button>

                    </div>

                    <div class="textarea-message -eva-3-mt-sm">{{(inputForUser || '').length}}/{{maxCharactersToInput}}</div>

                    <div *ngIf="feedback" class="feedback-container -eva-3-bc-white -eva-3-tc-white" (click)="openFeedback()">
                      <em class="feedback-message -eva-3-tc-brand-primary-3">{{feedback.message}}</em>
                      <div class="flex">
                        <i *ngFor="let i of [1,2,3,4,5]" class="eva-3-icon-star eva-3-icon -eva-3-icon-md -eva-3-tc-brand-primary-3"></i>
                      </div>
                    </div>
                </div>
            </div>
        </div>

        <p [ngStyle]="{'display': 'none'}" *ngIf="module.data?.meta_data?.session_id">{{module.data.meta_data.session_id}}</p>
    `,
    styleUrls: ['./chat-AI.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class ChatAIComponent extends AbstractComponent implements OnInit, AfterContentInit {
    @Input() override module: Module<ChatAIModuleConfiguration>;
    @ViewChild('rowsContainer', {read: ViewContainerRef, static: true}) rowsContainer: ViewContainerRef;
    @HostBinding('style.--card-size') cardSize = 200;

    static MAX_RETRIES: number = 4;
    inputForUser: string | undefined;
    messagesForService: ChatAIServiceMessage[] = [];
    messagesForUser: ChatAIMessage[] = [];
    searchLink: string | undefined;
    recomendations: string | undefined;

    disableButton: boolean;
    finishedConversation: boolean;
    onlySearchLink: boolean;
    isFixedInput: boolean;
    showSystemLoader: boolean;
    showSystemError: boolean;
    slowResponseMessage: boolean;
    responseOk: boolean;

    sliderWidth: number;
    fetchOfferRetries: number = ChatAIComponent.MAX_RETRIES;
    maxCharactersToInput: number = 500;
    maxInteractions: number = 45;
    moduleElement: any;

    hasShownStillHere = false;
    locale: string;
    domain: string;
    channel: string;
    country: string;
    pageviewId: string;
    currency: Currency;
    userAvatar: string | undefined;
    offerViewType: OfferViewType = OfferViewType.VERTICAL;
    offerCardType: OfferCardType = OfferCardType.REDUCED;
    upaData: any;
    lastMessageTimeStamp;
    secondsToWait = 40;
    prevMessagesLength = 0;
    lastResponse: ChatAIModuleConfiguration;
    feedback: ChatAIFeedback;
    assistantid: string;
    alreadyTrackedInit = false;

    constructor(injector: Injector,
        private chatAIService: ChatAIService,
        private translateService: TranslateService,
        private customTimmingsService: CustomTimmingsService,
        @Inject(PLATFORM_ID) private platformId: string) {

        super(injector);
        const landingModel: LandingModel = this.landingModelService.getLandingModel();
        this.userAvatar = landingModel.socialUser?.picture_url;
        this.currency = landingModel.currency;
    }

    ngOnInit() {
        if (isPlatformBrowser(this.platformId)) {
            this.channel = upaData.ch;
            this.country = upaData.cc;
            this.locale = window.locale;
            this.pageviewId = upaData.pageview_id;
        }

        if (this.module.data.version == 'V2') {
            this.trackInitComponent();

            this.assistantid = this.module.data.assistant_id ?? 'despegar';
            if (isPlatformBrowser(this.platformId)) {
                this.insertScript();
            }
        } else {
            this.showSystemLoader = true;
            this.feedback = this.module.data.feedback;
            setTimeout(() => this.init(), 2000)
        }
    }

    insertScript = () => {
        var timestampVersion = Date.now();
        const version = 'latest';
        const sofiaSource = `https://s3.staticontent.com/c4fe9d15/${version}/custom-loader.js?rnd=${timestampVersion}`;

        let $sofia = document.createElement('script');
        $sofia.src = sofiaSource;
        $sofia.async = true;

        $sofia.onload = () => {
            this.trackLoadedComponent();
            window.Sofia.load({
                locale: this.locale,
                assistantid: this.assistantid,
                channel: this.channel,
                site: this.country,
                pageviewid: this.pageviewId,
                sandbox: this.module.data.sandbox,
                version: version,
                placeholderElementId: 'chat-assistant',
                mode: 'FULL_SCREEN',
                googleMapsApiKey: this.module.data?.google_map_api_key,
            });
        }
    
        document.body.appendChild($sofia);
    }

    init() {
        this.messagesForService = [...this.module.data.messages];
        this.messagesForUser = [...this.module.data.messages];
        this.prevMessagesLength = this.module.data.messages.length;

        if (isPlatformBrowser(this.platformId)) {
            this.domain = window.location.hostname;
            this.module.data.meta_data.session_id = window.upaData.pageview_id;

            this.upaData = this.upaService.resolveData({label: '', uniqueId: this.module.unique_id, moduleId: this.module.id, moduleIndex: this.module.row_number, target: UpaTarget.CHAT_AI});
        }
        this.showSystemLoader = false;
    }

    ngAfterContentInit(): void {
        if (isPlatformBrowser(this.platformId)) {
            setTimeout(() => this.onScroll(), 300);
        }
    }

    trackInitComponent() {
        if (isPlatformBrowser(this.platformId) && !this.alreadyTrackedInit) {
            this.customTimmingsService.trackLandingTimings({
                "externalComponentInit": window.performance.now() / 1000,
            });
            this.alreadyTrackedInit = true;
        }
    }

    trackLoadedComponent() {
        if (isPlatformBrowser(this.platformId)) {
            this.customTimmingsService.trackLandingTimings({
                "externalComponentLoaded": window.performance.now() / 1000,
            });
        }
    }

    cleanInput = (inputHtml?: string) => {
      const htmlPattern = /<[^>]*>/g;
      return inputHtml?.replace(htmlPattern, '');
    }

    send() {
        this.lastMessageTimeStamp = Date.now();
        this.showSystemError = false;
        this.slowResponseMessage = false;
        this.responseOk = false;

        const inputValue = this.cleanInput(this.inputForUser);
        this.inputForUser = undefined;

        if (inputValue != null && inputValue.trim() != '') {
            this.disableButton = true;

            const userMessage = this.createNewMessage(inputValue, 'user');
            this.messagesForService.push(userMessage);
            this.messagesForUser.push(userMessage);

            this.showSystemLoader = true;

            this.scrollToBottom();

            const intervalId = setInterval(() => {
                this.slowResponseMessage = true;
                clearInterval(intervalId);
            }, 3000);

            if (this.messagesForService.length < this.maxInteractions) {
               this.fetch();
            } else {
              this.upaService.trackTopic('landing', {...this.upaData, ...{label: 'LIMIT_INTERACTIONS'}});
              const systemMessage = this.createNewMessage(this.translateService.instant('chatAI.limitInteractions'), 'system');
              systemMessage.showRestartButton = true;
              systemMessage.showVideoCallButton = true;
              this.finishedConversation = true;
              this.showSystemLoader = false;
              this.messagesForUser.push(systemMessage);
            }
        }
    }

    fetch() {
        this.chatAIService.getResults(this.messagesForService, this.module.data.meta_data, this.module.data.context, this.module.data.ai_type, this.domain, this.channel)
            .then((response) => {
                const typedResponse = response as ChatAIModuleConfiguration;
                this.lastResponse = typedResponse;
                this.responseOk = true;
                this.slowResponseMessage = false;
                this.showSystemLoader = false;

                if (typedResponse == null || Object.keys(typedResponse).length == 0 || typedResponse.messages == null) {
                    this.showSystemLoader = false;
                    this.showSystemError = true;
                    this.finishedConversation = true;

                } else {
                    this.module.data = typedResponse;

                    if (this.sliderWidth == null) {
                        this.sliderWidth = document.querySelector(".chat-container .message-container").offsetWidth - 16;
                    }

                    this.searchLink = typedResponse.search_link;
                    this.recomendations = typedResponse.recommendations;
                    const offers = typedResponse.offers;

                    this.finishedConversation = this.searchLink != null || this.recomendations != null
                        || typedResponse.messages[typedResponse.messages.length - 1].sender == 'directive'
                        || (offers != null && offers.length > 0);

                    if (this.searchLink != null && typedResponse.offers != null && typedResponse.offers.length > 0) {
                        this.endConversationWithOffers(typedResponse.offers);

                    } else if (this.finishedConversation) {
                        this.showSystemLoader = true;
                        this.drawOffersOrRetry(typedResponse);

                    } else {
                        this.showSystemLoader = false;
                        this.messagesForService = [...typedResponse.messages];
                        this.messagesForUser.push(typedResponse.messages[typedResponse.messages.length - 1]);
                    }

                    this.disableButton = this.finishedConversation;
                }

                this.startTimer();

                this.scrollToBottom();

            }, (error: HttpErrorResponse) => {
                this.responseOk = true;
                this.slowResponseMessage = false;
                this.showSystemLoader = false;

                if (error.status == 412) {
                    this.finishedConversation = true;
                    let message = this.createNewMessage(this.translateService.instant('chatAI.breakRules'), 'system');
                    message.showRestartButton = true;
                    this.messagesForUser.push(message);
                    this.upaService.trackTopic('landing', {...this.upaData, ...{label: `BREAKS_RULES|${this.messagesForService.length}`}});

                } else if (error.status == 413) {
                    this.finishedConversation = false;
                    this.disableButton = false;
                    let message = this.createNewMessage(this.translateService.instant('chatAI.offTopic'), 'system');
                    this.messagesForUser.push(message);
                    this.upaService.trackTopic('landing', {...this.upaData, ...{label: `OFFTOPIC|${this.messagesForService.length}`}});
                } else {
                    this.showSystemError = true;
                    this.finishedConversation = true;
                }
            }
        );
    }

    startTimer = () => {
      const nowTime = Date.now();
      this.lastMessageTimeStamp = nowTime;
      setTimeout(() => this.stillThere(nowTime), this.secondsToWait * 1000);
    }

    drawOffersOrRetry(response: ChatAIModuleConfiguration) {
        if (!response?.offers?.length) {
            this.fetchOfferRetries--;
            if (this.fetchOfferRetries >= 0) {
                this.chatAIService.getResults([], response.meta_data, response.context, response.ai_type, this.domain, this.channel, ['OFFERS'])
                    .then((response) => {
                      setTimeout(() => this.drawOffersOrRetry(response as ChatAIModuleConfiguration), 3000);

                    }, () => {
                        if (this.searchLink || this.recomendations) {
                            this.endConversationWithoutOffers();

                        } else {
                            this.showSystemLoader = false;
                            this.showSystemError = true;
                            this.finishedConversation = true;
                        }
                    });

            } else {
                this.endConversationWithoutOffers();
            }
        } else {
            this.endConversationWithOffers(response.offers);
        }
        this.scrollToBottom();
    }

    endConversationWithoutOffers() {
        this.showSystemLoader = false;
        this.onlySearchLink = true;
        const systemMessage = this.createNewMessage(this.translateService.instant('chatAI.offersFound'), 'system');
        systemMessage.search_link = this.searchLink;
        this.messagesForUser.push(systemMessage);

        this.upaService.trackTopic('landing', {...this.upaData, ...{label: `WIHOUT_OFFERS|${this.messagesForService.length}`}});

        if (this.recomendations != null && this.recomendations != '') {
            this.messagesForUser.push(this.createRecommendationMessage());
        } else {
            this.messagesForUser.push(this.createGoodbyeMessage());
            this.upaService.trackTopic('landing', {...this.upaData, ...{label: `WIHOUT_RECOMMENDATIONS|${this.messagesForService.length}`}});
        }

        this.scrollToBottom()
    }

    endConversationWithOffers(offers: any) {
        this.showSystemLoader = false;
        const systemMessage = this.createNewMessage(this.translateService.instant('chatAI.offersFound'), 'system');
        systemMessage.search_link = this.searchLink;
        systemMessage.offers = offers;
        this.messagesForUser.push(systemMessage);

        this.upaService.trackTopic('landing', {...this.upaData, ...{label: `SHOW_OFFERS|${this.messagesForService.length}`}});

        if (this.recomendations != null && this.recomendations != '') {
            this.messagesForUser.push(this.createRecommendationMessage());
        } else {
            this.messagesForUser.push(this.createGoodbyeMessage());
            this.upaService.trackTopic('landing', {...this.upaData, ...{label: `WIHOUT_RECOMMENDATIONS|${this.messagesForService.length}`}});
        }
        this.scrollToBottom()
    }

    createNewMessage(input: string | undefined, sender: string): ChatAIMessage {
        return { sender: sender, message: input, timestamp: 0, message_type: 'conversation' };
    }

    createRecommendationMessage() {
        const message = this.module.data.destination_description ?
            this.translateService.instant('chatAI.recommendationsWithDestination', {destination: this.module.data.destination_description})
            : this.translateService.instant('chatAI.recommendationsWithoutDestination');

        const systemMessage = this.createNewMessage(message, 'system');
        systemMessage.showRecommendationButton = true;
        systemMessage.showRestartButton = true;
        this.upaService.trackTopic('landing', {...this.upaData, ...{label: `HAS_RECOMMENDATIONS|${this.messagesForService.length}`}});

        return systemMessage;
    }

    createGoodbyeMessage() {
        if (this.recomendations) {
          this.messagesForUser.push(this.createNewMessage(this.recomendations, 'system'));
        }
        const systemMessage = this.createNewMessage(this.translateService.instant('chatAI.goodbye'), 'system');
        systemMessage.showRestartButton = true;
        return systemMessage;
    }

    showRecommendations() {
        this.messagesForUser.push(this.createGoodbyeMessage());
        this.scrollToBottom();
    }

    restart() {
        const systemMessage = this.createNewMessage(this.translateService.instant('chatAI.youCameBack'), 'system');
        this.messagesForService = [systemMessage];
        this.messagesForUser = [systemMessage];

        this.module.data.context = {};
        this.module.data.meta_data.conversation_id = uuid.v4();
        this.fetchOfferRetries = ChatAIComponent.MAX_RETRIES;

        this.finishedConversation = false;
        this.showSystemError = false;
        this.disableButton = false;
        this.hasShownStillHere = false;
        this.lastMessageTimeStamp = Date.now();
        window.scrollTo({top: 0, behavior: "smooth"});
    }

    getModuleId(): string {
        return 'ChatAIModule';
    }

    // EVENTS
    @HostListener('window:scroll') // for window scroll events
    onScroll() {
        if (this.screenType.toString() == 'MOBILE') {
            if (this.moduleElement == null) {
                this.moduleElement = document.querySelector(`#${this.module.unique_id}`);
            }
            this.isFixedInput = this.getBottomPosition() >= (window.pageYOffset + window.innerHeight);
        }
    }

    openFeedback() {
        this.upaService.trackTopic('landing', {...this.upaData, ...{label: `OPEN_FEEDBACK|${this.messagesForService.length}`}});
        window.open(this.feedback.link, "_blank");
    }

    getBottomPosition = () => {
      return this.moduleElement.offsetHeight + this.moduleElement.offsetTop;
    }

    onKeyUp(event: any) {
        if(event == null || (event.which === 13 || event.which === 45) && !event.shiftKey) {
            this.send();

        } else {
            const element = document.querySelector('.textarea-user-input');
            if (element.scrollHeight > 20) {
                element.style.height = 'auto';
                element.style.height = element.scrollHeight + 'px';
            }
        }
        this.startTimer();
    }

    onTouchStart() {
        setTimeout(() => this.scrollToBottom(), 500);
    }

    scrollToBottom() {
        if (this.screenType.toString() == 'MOBILE') {
            if (this.moduleElement == null) {
                this.moduleElement = document.querySelector(`#${this.module.unique_id}`);
            }
            setTimeout(() => {
                this.isFixedInput = this.screenType.toString() == 'MOBILE';
                window.scrollTo({top: this.getBottomPosition() - window.innerHeight, behavior: 'smooth'})
            }, 50);

        } else {
            setTimeout(() => {
                if (this.rowsContainer != null) {
                    this.rowsContainer.element.nativeElement.scrollTo({top: 50000, behavior: "smooth"});
                }
            }, 50);
        }
    }

    stillThere = (timeStamp) => {
      if (timeStamp == this.lastMessageTimeStamp && !this.finishedConversation) {
        if (this.hasShownStillHere) {
          this.showOffersAndCloseConversation(timeStamp)
        } else {
          this.messagesForUser.push(this.createNewMessage(this.translateService.instant('chatAI.stillThere'), 'system'));
          this.hasShownStillHere = true;
          this.scrollToBottom();
          this.upaService.trackTopic('landing', {...this.upaData, ...{label: `STILL_THERE|${this.messagesForService.length}`}});
          setTimeout(() => this.showOffersAndCloseConversation(timeStamp), this.secondsToWait * 1000);
        }
      }
    }

    showOffersAndCloseConversation = (timeStamp) => {
      this.chatAIService.getOfferWithDestination(this.locale, this.domain, this.lastResponse?.context?.destination)
        .then((destinationLink) => {
          if (timeStamp == this.lastMessageTimeStamp && !this.finishedConversation) {
            this.onlySearchLink = true;
            this.finishedConversation = true;
            let message = this.createNewMessage(this.translateService.instant('chatAI.showOffersAndCloseConversation'), 'system');
            if (destinationLink?.url) {
              this.upaService.trackTopic('landing', {...this.upaData, ...{label: `DESTINATION_LINK-${destinationLink.url}|${this.messagesForService.length}`}});
              message.search_link = destinationLink.url + '?from=chatAI';
            } else {
              this.upaService.trackTopic('landing', {...this.upaData, ...{label: `WITHOUT_DESTINATION_LINK|${this.messagesForService.length}`}});
            }
            message.showRestartButton = true;
            this.messagesForUser.push(message);
            this.scrollToBottom();
          }
        })

    }
}
